import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import SimpleLink from '../components/links/SimpleLink'
import { useRecoilState } from 'recoil'
import {
    AccountRecord,
    SocialMediaServiceEnum,
    useFetchHistoricYouTubeVideosMutation,
    useGetAccountQuery,
    useGetYouTubeSubscriptionDiagnosticUrlLazyQuery
} from '../api/client'
import Button from '../components/Button'
import DeleteAccountModal from '../components/DeleteAccountModal'
import { toastError, toastSuccess } from '../components/Notification'
import AccountTeamSelector from '../components/account/AccountTeamSelector'
import FetchContentSetting from '../components/account/FetchContentSetting'
import FetchHistoricMeta from '../components/account/FetchHistoricMeta'
import RetirePostDaysSetting from '../components/account/RetirePostDaysSetting'
import { fetchContentExplanation } from '../components/account/fetchContent'
import { fetchPostsExplanation } from '../components/account/fetchPosts'
import { retirePostDaysExplanation } from '../components/account/retirePostDays'
import { ShadowBanningSettings } from '../components/account/settings/shadow-banning/ShadowBanningSettings'
import AuthoriseButton from '../components/authorise/AuthoriseButton'
import Card from '../components/card/Card'
import DuplicateModerationConfiguration from '../components/moderation/DuplicateModerationConfiguration'
import AutoDeleteSevereContentToggle from '../components/toggles/AutoDeleteSevereContentToggle'
import EnableScreenshotsToggle from '../components/toggles/EnableScreenshotsToggle'
import AutoHideAdCommentsToggle from '../components/toggles/AutoHideAdCommentsToggle'
import AutoHideSevereContentToggle from '../components/toggles/AutoHideSevereContentToggle'
import AutoMuteSevereAccountToggle from '../components/toggles/AutoMuteSevereAccountToggle'
import DmsToggle from '../components/toggles/DmsToggle'
import FetchAdsToggle from '../components/toggles/FetchAdsToggle'
import FetchPostsToggle from '../components/toggles/FetchPostsToggle'
import LlmClassificationToggle from '../components/toggles/LlmClassificationToggle'
import LlmReplyToggle from '../components/toggles/LlmReplyToggle'
import ProfileCollectionToggle from '../components/toggles/ProfileCollectionToggle'
import AccountMonitoringCard from '../components/scheduledJobs/AccountMonitoringCard'
import ProfileModerationToggle from '../components/toggles/ProfileModerationToggle'
import PageMasthead from '../components/PageMasthead'
import MastheadBreadcrumb from '../components/MastheadBreadcrumb'
import { useTitle } from '../components/TitleProvider'
import { isPlatformAppReviewerState, keywordsEnabledState } from '../store/DomainStore'
import { getUserPermissions } from '../util/auth'
import KeywordListTable from './admin/KeywordListTable'

type ParamTypes = {
    accountId: string
}

/**
 * Checks whether this account is a meta account (i.e. Instagram or Facebook)
 */
export function isMeta(account: AccountRecord) {
    const socialMediaService = account.socialMediaServiceShortName?.toLowerCase()
    return (
        socialMediaService === SocialMediaServiceEnum.Instagram.toLowerCase() ||
        socialMediaService === SocialMediaServiceEnum.Facebook.toLowerCase()
    )
}

export function areDmsSupported(account: AccountRecord) {
    const socialMediaService = account.socialMediaServiceShortName?.toLowerCase()
    return socialMediaService === SocialMediaServiceEnum.Facebook.toLowerCase()
}

/**
 * This page holds all the settings for a given account.
 */
function AccountSettingsPage() {
    useTitle('Profile settings')
    const location = useLocation()
    const { accountId } = useParams<ParamTypes>()
    const [admin, setAdmin] = useState(false)
    const [isPlatformAppReviewer] = useRecoilState(isPlatformAppReviewerState)
    const [keywordsEnabled] = useRecoilState(keywordsEnabledState)
    const [deleteAccountModalVisible, setDeleteAccountModalVisible] = useState(false)

    const [fetchHistoricYouTubeVideos] = useFetchHistoricYouTubeVideosMutation()
    const [getYouTubeSubscriptionDiagnosticUrl, { data: subscriptionData }] =
        useGetYouTubeSubscriptionDiagnosticUrlLazyQuery()

    useEffect(() => {
        // Check if there's a hash in the URL (e.g., #section-name)
        if (location.hash) {
            const element = document.getElementById(location.hash.replace('#', ''))
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        }
    }, [location])

    const { data: accountData, refetch } = useGetAccountQuery({
        variables: { accountId: parseInt(accountId as string) }
    })

    const account = accountData?.getAccount

    let showMetaFetch = false
    if (account) {
        showMetaFetch = isMeta(account) ? isPlatformAppReviewer || admin : false
    }

    let showDmToggle = false
    if (account) {
        showDmToggle = areDmsSupported(account)
    }

    const fetchContentHere = fetchContentExplanation(account?.socialMediaServiceShortName)

    const retirePostDaysHere = retirePostDaysExplanation(account?.socialMediaServiceShortName)

    const fetchPostsHere = fetchPostsExplanation(account?.socialMediaServiceShortName)

    useEffect(() => {
        if (account) {
            getYouTubeSubscriptionDiagnosticUrl({
                variables: { channelId: account.serviceAccountId }
            })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account])

    useEffect(() => {
        async function determineIfAdmin() {
            const userPermissions = await getUserPermissions()

            setAdmin(userPermissions.isAdmin)
        }
        determineIfAdmin()
    }, [])

    let diagnosticUrl = useRef('')
    useEffect(() => {
        const url = subscriptionData?.getYouTubeSubscriptionDiagnosticUrl?.url
        if (url) {
            diagnosticUrl.current = url
        }
    }, [subscriptionData])

    const handleDeleteClick = () => {
        setDeleteAccountModalVisible(true)
    }

    const handleTeamChange = () => {
        refetch()
    }

    return (
        <div className="min-h-full bg-ink-50">
            <PageMasthead
                title="Profile settings"
                subtitle="Teach Arwen how to moderate this profile."
                breadcrumb={
                    <MastheadBreadcrumb
                        to={`/account/${accountId}`}
                        label={account?.accountName ?? 'Profile'}
                    />
                }
            />
            <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 lg:px-8">
            {account && (
                <section className="space-y-10">
                    <div>
                        <div className="mt-2 grid grid-cols-1 gap-6 lg:grid-cols-1">
                            {!account.hasAuthorisation && (
                                <Card
                                    title="Authorisation"
                                    description="This profile has no authorisation - authorise this profile to enable automations."
                                    children={
                                        <AuthoriseButton
                                            account={account}
                                            onlyWhenUnauthorised={true}
                                        />
                                    }
                                />
                            )}
                            <Card
                                title="Auto-moderation"
                                description="Choose how you want Arwen to auto-moderate Severe comments that breach your policy."
                            >
                                <div className="flex flex-col gap-3">
                                    <AutoHideSevereContentToggle account={account} />
                                    <AutoDeleteSevereContentToggle account={account} />
                                    {/* We will not expose these settings to the user at this time. They are available for the admins below.*/}
                                    {/* <AutoMuteSevereAccountToggle account={account} />
                                    <AutoHideAdCommentsToggle account={account} /> */}
                                </div>
                            </Card>

                            <Card
                                title="Shadow banning"
                                description="Automatically moderate all comments from specific Commenters after they cross a “repeat offender” threshold. Auto-hiding needs to be enabled for this to work. It takes 5-10 minutes to activate."
                            >
                                <ShadowBanningSettings account={account} />
                            </Card>

                            <Card
                                id="keywords-card"
                                title="Keywords"
                                description="Arwen uses a range of moderation technologies to keep your social profiles safe. We use 'keywords' selectively to detect and moderate comments that contain overtly toxic keywords or phrases. Use this page to add, remove or change keywords for this profile"
                            >
                                <p className="text-sm text-gray-600 mb-4">
                                    Keywords are case-insensitive
                                </p>
                                <KeywordListTable
                                    accountId={account.id}
                                    admin={admin}
                                    keywordsEnabled={keywordsEnabled}
                                />
                            </Card>

                            {admin && (
                                <>
                                    <hr />

                                    {/* Shown on authorised accounts too, with its
                                        options disabled: a section that vanishes
                                        cannot explain why, and "is anything
                                        collecting for this profile?" needs an
                                        answer on every profile. */}
                                    {account.socialMediaServiceShortName &&
                                    account.serviceAccountUsername ? (
                                        <Card
                                            id="monitoring-card"
                                            title="Admins: un-authorised collection"
                                            description="Collect this account's comments by scraping, on a daily schedule. For accounts Arwen has no authorisation for. Supports the Arwen Monitor service."
                                        >
                                            <AccountMonitoringCard
                                                accountId={account.id}
                                                hasAuthorisation={account.hasAuthorisation}
                                                platform={account.socialMediaServiceShortName}
                                                serviceAccountUsername={
                                                    account.serviceAccountUsername
                                                }
                                            />
                                        </Card>
                                    ) : null}

                                    <Card
                                        title="Admins: Auto-moderation for admins"
                                        description="Choose how you want Arwen to auto-moderate Severe comments that breach your policy."
                                    >
                                        <div className="flex flex-col gap-3">
                                            <AutoMuteSevereAccountToggle account={account} />
                                            <AutoHideAdCommentsToggle account={account} />
                                        </div>
                                    </Card>

                                    <Card
                                        title="Admins: Team"
                                        children={
                                            <AccountTeamSelector
                                                account={account}
                                                onTeamChange={handleTeamChange}
                                            />
                                        }
                                    ></Card>
                                    <Card
                                        title="Admins: Screenshots"
                                        description="Control whether screenshots are automatically captured for content from this account."
                                    >
                                        <div className="w-80">
                                            <EnableScreenshotsToggle account={account} />
                                        </div>
                                    </Card>
                                    <Card title={'Admins:  Profile subscription and authorisation'}>
                                        <p className="mt-0">
                                            The following link lets you check the authorisation and
                                            subscription status and repair/enable/disable
                                            subscriptions.
                                        </p>
                                        <SimpleLink
                                            to={`/account/${accountId}/settings/validation`}
                                        >
                                            Profile subscription and authorisation page
                                        </SimpleLink>
                                    </Card>

                                    <Card
                                        title={'Admins: Notification configuration'}
                                        description="Configure how and when notifications are sent for this profile."
                                        children={
                                            <SimpleLink
                                                to={`/account/${accountId}/settings/notifications`}
                                            >
                                                Notification configuration page
                                            </SimpleLink>
                                        }
                                    />

                                    {isMeta(account) && (
                                        <Card
                                            title={'Admins: Meta ad-account visibility'}
                                            description="Live diagnostic for the Meta ad accounts our token can see — use when ad-comment classification looks off (EN-210)."
                                            children={
                                                <SimpleLink
                                                    to={`/account/${accountId}/settings/meta-ad-visibility`}
                                                >
                                                    Ad-account visibility page
                                                </SimpleLink>
                                            }
                                        />
                                    )}

                                    <Card
                                        title={'Admins: Keywords and Boundaries'}
                                        children={
                                            <div className="flex flex-col">
                                                <SimpleLink
                                                    to={`/account/${accountId}/moderation-boundaries`}
                                                >
                                                    Edit moderation boundaries...
                                                </SimpleLink>

                                                <DuplicateModerationConfiguration
                                                    account={account}
                                                />
                                            </div>
                                        }
                                    />

                                    {showDmToggle ? (
                                        <Card
                                            title="Admins: Direct messages"
                                            children={
                                                <>
                                                    {' '}
                                                    <p>
                                                        Disable or enable DMs for this account.
                                                        'Fetch conversations' will get existing
                                                        conversations and messages.
                                                    </p>
                                                    <div className="w-120">
                                                        <DmsToggle account={account} />
                                                    </div>
                                                </>
                                            }
                                        />
                                    ) : undefined}

                                    <Card
                                        title="Admins: LLM settings"
                                        children={
                                            <>
                                                <h3 id="llm-settings">Reply</h3>
                                                <div className="max-w-96 w-96">
                                                    <LlmReplyToggle account={account} />
                                                </div>
                                                <SimpleLink
                                                    to={`/account/${accountId}/settings/llm-reply`}
                                                >
                                                    Edit LLM reply settings...
                                                </SimpleLink>
                                                <h3 className="mt-3" id="llm-classification">
                                                    Classification
                                                </h3>
                                                <div className="max-w-96 w-96">
                                                    <LlmClassificationToggle account={account} />
                                                </div>
                                                {/* Classification prompts moved to the versioned
                                                    ClassifierPrompt admin UI (EN-467); the per-profile
                                                    LlmPrompt editor that lived here is gone. */}
                                                <SimpleLink to="/admin/classifiers">
                                                    Manage classification prompts...
                                                </SimpleLink>
                                            </>
                                        }
                                    />

                                    {fetchContentHere && (
                                        <Card title="Admins: Fetch with API">
                                            <FetchContentSetting
                                                account={account}
                                                explanation={fetchContentHere}
                                            />
                                        </Card>
                                    )}
                                    {account.socialMediaServiceShortName?.toLowerCase() ===
                                        SocialMediaServiceEnum.Twitter.toLowerCase() && (
                                        <Card
                                            title="Admins: X historic content"
                                            description="Backfill this profile's X replies through the moderation pipeline. Reads are billed on our pay-per-use X plan — see the fetch page for limitations and costs."
                                            children={
                                                <SimpleLink
                                                    to={`/account/${accountId}/fetch-historic`}
                                                >
                                                    Fetch historic X content...
                                                </SimpleLink>
                                            }
                                        />
                                    )}
                                    {account.socialMediaServiceShortName?.toLowerCase() ===
                                        SocialMediaServiceEnum.Instagram.toLowerCase() && (
                                        <Card
                                            title="Admins: Instagram settings"
                                            children={
                                                <div className="w-4/5">
                                                    <ProfileCollectionToggle account={account} />
                                                    <ProfileModerationToggle account={account} />
                                                </div>
                                            }
                                        />
                                    )}
                                    {account.socialMediaServiceShortName?.toLowerCase() ===
                                        SocialMediaServiceEnum.Youtube.toLowerCase() && (
                                        <Card
                                            title="Admins: YouTube settings"
                                            children={
                                                <>
                                                    <Link
                                                        key={account.id}
                                                        to={`/account/${accountId}/authorisation/revoke`}
                                                        className="text-primary-600 underline ml-3"
                                                    >
                                                        Revoke authorisation...
                                                    </Link>
                                                    <a
                                                        href={diagnosticUrl.current}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="ml-3"
                                                    >
                                                        <span className="flex">
                                                            YouTube subscription diagnostics page...
                                                            <ArrowTopRightOnSquareIcon className="w-4" />
                                                        </span>
                                                    </a>
                                                    <div className="flex flex-col gap-y-3 ml-3 mt-3">
                                                        {/* I've removed the following buttons as they are now obsolete and haven't been used in years
                                    - Add or renew YouTube post subscription
                                    - Refresh access token
                                     */}
                                                        <div>
                                                            <span className="text-gray-600 text-sm mb-1">
                                                                We only fetch comments on videos we
                                                                know about. We get sent videos via
                                                                our webhook, but that means we only
                                                                have videos that were created since
                                                                the profile was added to Arwen. Our
                                                                customers frequently want us to
                                                                collect comments on existing videos.
                                                                This function uses the API to fetch
                                                                older videos, so we can fetch older
                                                                comments, and indeed new comments on
                                                                older videos. This action fetches 5
                                                                years of posts and could take up to
                                                                45 minutes.
                                                            </span>

                                                            <div className="w-80">
                                                                <Button
                                                                    primary={false}
                                                                    text="Fetch historic YouTube videos"
                                                                    onClick={() =>
                                                                        fetchHistoricYouTubeVideos({
                                                                            variables: {
                                                                                accountId:
                                                                                    account.id
                                                                            },
                                                                            onCompleted: () => {
                                                                                toastSuccess(
                                                                                    'Submitted fetch request for historic YouTube videos.'
                                                                                )
                                                                            },
                                                                            onError: (error) => {
                                                                                console.error(error)
                                                                                toastError(
                                                                                    'Unable to request historic YouTube videos! See console for errors.'
                                                                                )
                                                                            }
                                                                        })
                                                                    }
                                                                ></Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            }
                                        />
                                    )}
                                    {showMetaFetch && (
                                        <Card
                                            title="Admins: Meta content fetching settings"
                                            children={
                                                <>
                                                    <h2>Meta</h2>
                                                    {fetchPostsHere && (
                                                        <>
                                                            <div className="w-80">
                                                                <FetchPostsToggle
                                                                    account={account}
                                                                    tooltip={fetchPostsHere}
                                                                />
                                                            </div>
                                                            <p className="ml-3 mt-1 text-gray-600 ">
                                                                {fetchPostsHere}
                                                            </p>
                                                        </>
                                                    )}
                                                    <div className="w-80 ">
                                                        <FetchAdsToggle account={account} />
                                                    </div>
                                                    <p className="ml-3 mt-1 text-gray-600 ">
                                                        Continuously fetch new and existing{' '}
                                                        <strong>active</strong> ads for this
                                                        profile, and store them as Posts. Allows
                                                        Arwen to detect ad comments.
                                                    </p>
                                                    <div className="w-80 ml-3">
                                                        <FetchHistoricMeta account={account} />
                                                    </div>
                                                    <p className="ml-3 mt-1 text-gray-600 ">
                                                        This will fetch 24 hours of historic content
                                                        for Meta profiles
                                                    </p>
                                                </>
                                            }
                                        />
                                    )}
                                    {retirePostDaysHere && (
                                        <Card title="Admins: How long we collect a post">
                                            <RetirePostDaysSetting
                                                account={account}
                                                explanation={retirePostDaysHere}
                                            />
                                        </Card>
                                    )}
                                    <Card
                                        title="Admins: Delete this profile"
                                        children={
                                            <>
                                                {deleteAccountModalVisible ? (
                                                    <DeleteAccountModal
                                                        setVisible={setDeleteAccountModalVisible}
                                                        accountId={account.id}
                                                    />
                                                ) : null}
                                                <div className="w-80 ml-3">
                                                    <Button
                                                        primary={false}
                                                        onClick={handleDeleteClick}
                                                        text="Delete profile forever"
                                                    />
                                                </div>
                                                <p className="ml-3 mt-1 text-gray-600">
                                                    Deleting a profile can take some time, e.g.
                                                    15-30 minnutes. Once deleted this profile will
                                                    disappear from the profile list but will remain
                                                    in a hidden state until all its content and
                                                    classifications have been deleted. During this
                                                    time of you try to add the profile again you
                                                    will get an error
                                                </p>
                                            </>
                                        }
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </section>
            )}
            </div>
        </div>
    )
}

export default AccountSettingsPage
